import { AlertTriangle, Check, Download, Film, Play, Scissors } from 'lucide-react';

import { Button, Pill } from '../../components/ui';
import type { CutCandidate } from '../../types/api';
import { candidateFootnote, durationLabel, timecode, verdictTone } from './contract';
import styles from './CutPage.module.css';

interface Props {
  candidate: CutCandidate;
  busy?: boolean;
  actionsHint?: string;
  onExtract?: (candidate: CutCandidate) => void;
  onPlay?: (candidate: CutCandidate) => void;
}

export function CandidateCard({ candidate, busy = false, actionsHint, onExtract, onPlay }: Props) {
  const tone = verdictTone(candidate.score);
  const disabled = Boolean(actionsHint) || busy;

  return (
    <article className={styles.card}>
      <div className={styles.frame}>
        {candidate.thumbnail ? (
          <img
            className={styles.frameImage}
            src={candidate.thumbnail.url}
            alt={`Кадр фрагмента с ${timecode(candidate.start_s)}`}
            loading="lazy"
          />
        ) : (
          <Film size={24} strokeWidth={1.2} aria-hidden="true" />
        )}
        <span className={styles.rangeBadge}>
          {timecode(candidate.start_s)} – {timecode(candidate.end_s)}
        </span>
        <span className={styles.durBadge}>{durationLabel(candidate.duration_s)}</span>
        <span className={styles.rankBadge}>#{candidate.index}</span>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.scoreRow}>
          <span title="Можно сравнивать с кусками из другого файла">
            <Pill tone={tone}>{Math.round(candidate.score * 100)}%</Pill>
          </span>
          <span className={styles.verdict}>{candidate.verdict}</span>
        </div>

        <ul className={styles.checks}>
          {candidate.checks.map((check) => (
            <li
              key={check.key}
              className={`${styles.check} ${check.ok ? styles.checkOk : styles.checkWarn}`}
            >
              {check.ok ? (
                <Check size={12} strokeWidth={2} aria-hidden="true" />
              ) : (
                <AlertTriangle size={12} strokeWidth={2} aria-hidden="true" />
              )}
              <span className={styles.checkLabel} title={check.label}>
                {check.label}
              </span>
            </li>
          ))}
        </ul>

        <div className={styles.cardFoot}>
          <span className={styles.cardFootMeta} title="кадров · разброс поворота головы">
            {candidateFootnote(candidate)}
          </span>
          <div className={styles.iconButtons}>
            <Button
              size="xs"
              iconOnly
              title="Посмотреть"
              aria-label="Посмотреть"
              onClick={() => onPlay?.(candidate)}
            >
              <Play size={13} strokeWidth={1.5} />
            </Button>
            {candidate.extracted && candidate.clip ? (
              <a
                className={styles.iconLink}
                href={candidate.clip.url}
                download
                title="Скачать"
                aria-label="Скачать"
              >
                <Download size={13} strokeWidth={1.5} />
              </a>
            ) : (
              <Button
                size="xs"
                variant="primary"
                disabled={disabled}
                title={actionsHint}
                onClick={() => onExtract?.(candidate)}
              >
                <Scissors size={13} strokeWidth={1.5} />
                {busy ? 'режем…' : 'Вырезать'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
